import { useEffect, useMemo, useRef, useState } from 'react';
import { loadSearchIndex } from './skyCatalogLoader';
import { normalizeDesignation, normalizeName } from '../../utils/catalogNames';
import './targetSearch.scss';

const MAX_RESULTS = 8;

// Normalized forms computed once per record rather than on every keystroke.
const prepare = (index) => index.map((target) => ({
    target,
    ids: [target.label, ...(target.ids || [])].map(normalizeDesignation),
    names: (target.names || (target.common ? [target.common] : []))
        .map((name) => ({ name, norm: normalizeName(name) }))
}));

// Best match first: an exact designation ("IC 434"), then designations that
// start with the query ("M3" -> M31, M33), then nicknames that start with it,
// then nicknames that merely contain it ("Horsehead" -> Blue Horsehead).
const rank = (entry, qId, qName) => {
    let best = null;
    const consider = (score, matchedName) => {
        if (!best || score < best.score) best = { score, matchedName };
    };
    entry.ids.forEach((id) => {
        if (id === qId) consider(0, null);
        else if (qId && id.startsWith(qId)) consider(1 + id.length / 100, null);
    });
    entry.names.forEach(({ name, norm }) => {
        if (norm.startsWith(qName)) consider(2, name);
        else if (norm.includes(qName)) consider(3, name);
    });
    return best;
};

const matches = (prepared, query) => {
    const qId = normalizeDesignation(query);
    const qName = normalizeName(query);
    if (qName.length < 2) return [];
    return prepared
        .map((entry) => ({ entry, match: rank(entry, qId, qName) }))
        .filter(({ match }) => match)
        .sort((a, b) => a.match.score - b.match.score)
        .slice(0, MAX_RESULTS)
        .map(({ entry, match }) => ({
            ...entry.target,
            // Show the nickname that matched ("Pickering's Triangle") rather
            // than the object's primary name when they differ.
            shownName: match.matchedName || entry.target.common
        }));
};

const TargetSearch = ({ onSelect, selectedTarget }) => {
    const [index, setIndex] = useState(null);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        loadSearchIndex().then((idx) => setIndex(prepare(idx)));
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const clear = () => {
        setQuery('');
        setConfirmed(false);
        onSelect(null);
        inputRef.current?.focus();
    };

    const results = useMemo(() => (index ? matches(index, query) : []), [index, query]);

    return (
        <div className="target-search" ref={containerRef}>
            <label className="fov-field-label" htmlFor="target-search-input">Target</label>
            <div className="target-search-input-row">
                <input
                    id="target-search-input"
                    ref={inputRef}
                    type="text"
                    autoComplete="off"
                    placeholder={index ? 'Search by name, e.g. M31 or Andromeda' : 'Loading targets…'}
                    value={query}
                    disabled={!index}
                    onChange={(e) => { setQuery(e.target.value); setConfirmed(false); setOpen(true); }}
                    onFocus={() => {
                        // Re-opening a confirmed target should start a fresh
                        // search, not force backspacing through the old label.
                        if (confirmed) {
                            setQuery('');
                            setConfirmed(false);
                        }
                        setOpen(true);
                    }}
                />
                {query ? (
                    <button
                        type="button"
                        className="target-search-clear"
                        aria-label="Clear target"
                        onClick={clear}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                ) : null}
            </div>

            {open && query.trim().length >= 2 ? (
                <div className="target-search-results">
                    {results.length ? (
                        results.map((r) => (
                            <button
                                type="button"
                                key={r.key}
                                className="target-search-result"
                                onClick={() => {
                                    const { shownName, ...target } = r;
                                    onSelect(target);
                                    setQuery(shownName ? `${r.label} - ${shownName}` : r.label);
                                    setConfirmed(true);
                                    setOpen(false);
                                }}
                            >
                                <span className="target-search-result-label">{r.label}</span>
                                {r.shownName ? <span className="target-search-result-common">{r.shownName}</span> : null}
                            </button>
                        ))
                    ) : (
                        <p className="target-search-empty">No targets match &ldquo;{query}&rdquo;.</p>
                    )}
                </div>
            ) : null}

            {selectedTarget && !open ? (
                <p className="target-search-selected">
                    Framing <strong>{selectedTarget.label}</strong>
                    {selectedTarget.common ? ` (${selectedTarget.common})` : ''}
                </p>
            ) : null}
        </div>
    );
};

export default TargetSearch;
